/**
 * The dashboard that answers in five seconds (SPEC §6.1).
 *
 * A parent opens this to know **"est-ce que ça va ?"**. That answer is above
 * the fold, per child, with the "attendu" mark drawn on every gauge — 50 means
 * "at the programme's level on this date", and without the mark a gauge at 55
 * means nothing (invariant 10).
 *
 * When `mastery` has not measured anything, the screen says so. It does not
 * draw a gauge at 50 % and let a parent believe it.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { ChildSummary, Dashboard } from "@/api/schema";
import { subjectLabel } from "@/config/wording";
import { chargerLesEnfants } from "@/ui/enfants";
import { readProgression, fragile, type Progression } from "@/parent/progression";
import { Gauge } from "@/ui/Gauge";
import { Card, ErrorNote, Loading, Screen } from "@/ui/primitives";
import { etiquette } from "@/ui/matieres";

type Row = { child: ChildSummary; dashboard: Dashboard | null; progression: Progression | null };

export function DashboardScreen() {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const children = await chargerLesEnfants();
        const loaded = await Promise.all(
          children.map(async (child): Promise<Row> => {
            try {
              const dashboard = await call("dashboard", { params: { child_id: child.child_id } });
              return { child, dashboard, progression: readProgression(dashboard.progression) };
            } catch {
              return { child, dashboard: null, progression: null };
            }
          }),
        );
        if (!cancelled) setRows(loaded);
      } catch (failure) {
        if (!cancelled) {
          setError(failure instanceof ApiError ? failure.message : "Chargement impossible.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!rows) return <Loading />;

  return (
    <Screen title="Est-ce que ça va ?">
      {rows.map(({ child, dashboard, progression }) => (
        <Card key={child.child_id} title={child.display_name.toUpperCase()}>
          <p className="muted small">
            {child.level} · {child.subjects.map(subjectLabel).join(" · ")}
          </p>

          {progression ? (
            <>
              {progression.subjects.map((subject) => (
                <Gauge
                  key={subject.subject}
                  label={etiquette(subject.subject, subject.label)}
                  score={subject.score}
                  expected={subject.expected}
                  observations={subject.observations}
                />
              ))}
              {fragile(progression).length > 0 ? (
                <p className="attention">
                  {fragile(progression).length} compétence
                  {fragile(progression).length > 1 ? "s" : ""} fragile
                  {fragile(progression).length > 1 ? "s" : ""}.{" "}
                  <Link to={`/parents/enfant/${child.child_id}`}>Voir lesquelles</Link>
                </p>
              ) : null}
            </>
          ) : (
            <p className="muted">
              {dashboard?.measured
                ? "Progression indisponible pour le moment."
                : "Pas encore de mesure. Rien n'est affiché tant que rien n'a été observé."}
            </p>
          )}

          <p className="row-links">
            <Link to={`/parents/enfant/${child.child_id}`}>Détail</Link>
            <Link to={`/parents/enfant/${child.child_id}/resumes`}>Résumés de séance</Link>
            <Link to={`/parents/enfant/${child.child_id}/echanger`}>Poser une question</Link>
          </p>
        </Card>
      ))}
    </Screen>
  );
}
